import * as stylex from '@stylexjs/stylex'
import type { ComponentPropsWithRef } from 'react'

import { colors, fonts, fontSizes, spacing } from '../tokens/tokens.stylex'

const styles = stylex.create({
  container: {
    width: '100%',
    overflowX: 'auto',
    outlineColor: colors.foreground,
    outlineOffset: 2,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    textAlign: 'left',
    fontFamily: fonts.sans,
    fontSize: fontSizes.small,
    color: colors.foreground,
  },
  fixed: { tableLayout: 'fixed' },
  head: { borderBottomWidth: 1, borderBottomStyle: 'solid', borderBottomColor: colors.hairline },
  row: {
    borderBottomWidth: { default: 1, ':last-child': 0 },
    borderBottomStyle: 'solid',
    borderBottomColor: colors.hairline,
  },
  cell: {
    paddingBlock: 'var(--table-cell-padding)',
    paddingInlineStart: { default: spacing.space8, ':first-child': 0 },
    paddingInlineEnd: { default: spacing.space8, ':last-child': 0 },
    verticalAlign: 'var(--table-cell-align, top)',
    textAlign: 'start',
  },
  compact: { '--table-cell-padding': spacing.space6 },
  normal: { '--table-cell-padding': spacing.space8 },
  collection: { '--table-cell-align': 'middle', '--table-row-height': '40px', '--table-row-hover': colors.hover },
  collectionRow: {
    height: 'var(--table-row-height, auto)',
    backgroundColor: {
      default: 'transparent',
      ':hover': 'var(--table-row-hover, transparent)',
      ':focus-within': 'var(--table-row-hover, transparent)',
    },
  },
  selected: { backgroundColor: { default: colors.surfaceAlt, ':hover': colors.surfaceAlt } },
  header: { color: colors.placeholder, fontSize: fontSizes.caption, fontWeight: 500, paddingBlock: spacing.space6 },
  rowHeader: { fontWeight: 400 },
  end: { textAlign: 'end', fontVariantNumeric: 'tabular-nums' },
  truncate: { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
})

type NativeProps<Tag extends 'section' | 'table' | 'thead' | 'tbody' | 'tr' | 'td' | 'th'> = Omit<
  ComponentPropsWithRef<Tag>,
  'className' | 'style' | 'align' | 'color'
>

type TableContainerName = { 'aria-label': string }

export type TableContainerProps = NativeProps<'section'> & TableContainerName

type TableOptions = {
  density?: 'normal' | 'compact'
  layout?: 'auto' | 'fixed'
  variant?: 'default' | 'collection'
}

export type TableProps = NativeProps<'table'> & TableOptions
export type TableHeadProps = NativeProps<'thead'>
export type TableBodyProps = NativeProps<'tbody'>

export interface TableRowProps extends NativeProps<'tr'> {
  selected?: boolean
}

type TableCellOptions = {
  align?: 'start' | 'end' | undefined
  truncate?: boolean
}

export type TableCellProps = NativeProps<'td'> & TableCellOptions
export type TableHeaderCellProps = NativeProps<'th'> & TableCellOptions

/**
 * Provide named, keyboard-focusable horizontal scrolling around a table. Use ordinary layout wrappers when
 * content needs no tabular overflow region. Applications supply the accessible region name.
 *
 * Status: supported.
 */
export function TableContainer(props: TableContainerProps) {
  // oxlint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- The named overflow region needs focus for keyboard scrolling.
  return <section tabIndex={0} {...props} {...stylex.props(styles.container)} />
}

/**
 * Compare records across common columns using native table semantics. Use cards or a list when records have
 * different fields. Applications supply records, ordering, sorting state, links, and empty or failed states.
 * Choose fixed layout with explicit column widths when long values should not resize the collection.
 * The collection variant centers cells in compact rows and highlights hover and keyboard focus.
 *
 * Status: supported.
 */
export function Table({ density = 'normal', layout = 'auto', variant = 'default', ...props }: TableProps) {
  return (
    <table
      {...props}
      data-density={density}
      {...stylex.props(
        styles.table,
        styles[density],
        layout === 'fixed' && styles.fixed,
        variant === 'collection' && styles.collection
      )}
    />
  )
}

/**
 * Group a table's column-heading rows; use TableBody for record rows. Applications supply TableHeaderCell
 * labels and their relationships to data columns.
 *
 * Status: supported.
 */
export function TableHead(props: TableHeadProps) {
  return <thead {...props} {...stylex.props(styles.head)} />
}

/**
 * Group the supplied record rows; use TableHead for column headings. Applications decide row order,
 * filtering, and pagination before rendering.
 *
 * Status: supported.
 */
export function TableBody(props: TableBodyProps) {
  return <tbody {...props} />
}

/**
 * Keep one record's fields or one set of headings in a native table row. Applications provide stable record
 * identity and use TableHeaderCell for row labels.
 *
 * Status: supported.
 */
export function TableRow({ selected = false, ...props }: TableRowProps) {
  return (
    <tr
      {...props}
      aria-selected={selected || undefined}
      {...stylex.props(styles.row, styles.collectionRow, selected && styles.selected)}
    />
  )
}

/**
 * Present a record value inside a TableRow; use TableHeaderCell when the cell labels a row or column.
 * Applications format values and align numeric columns consistently. Supply the full value through title when truncating.
 *
 * Status: supported.
 */
export function TableCell({ align = 'start', truncate = false, ...props }: TableCellProps) {
  return <td {...props} {...stylex.props(styles.cell, align === 'end' && styles.end, truncate && styles.truncate)} />
}

/**
 * Label a table column or record row; use TableCell for ordinary values. Applications supply scope, sort
 * state through aria-sort, and any sorting action.
 *
 * Status: supported.
 */
export function TableHeaderCell({ scope = 'col', align = 'start', truncate = false, ...props }: TableHeaderCellProps) {
  return (
    <th
      {...props}
      scope={scope}
      {...stylex.props(
        styles.cell,
        scope === 'row' ? styles.rowHeader : styles.header,
        align === 'end' && styles.end,
        truncate && styles.truncate
      )}
    />
  )
}
